import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { apiFetch } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Bell as BellIcon,
  Check as CheckIcon,
  Package as PackageIcon,
  CurrencyDollar as CurrencyDollarIcon,
  Users as UsersIcon,
  Gear as GearIcon
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';

interface UserBasicInfo {
  id: string;
  email: string;
  full_name?: string;
}

interface Notification {
  id: string;
  title: string;
  message: string;
  category: string;
  severity: string;
  action_url?: string;
  action_label?: string;
  created_at: string;
  is_acknowledged: boolean;
  acknowledged_by?: UserBasicInfo;
  acknowledged_at?: string;
}

const severityColors = {
  info: 'bg-blue-100 text-blue-800 border-blue-200',
  warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  critical: 'bg-red-100 text-red-800 border-red-200',
};

const categoryColors = {
  inventory: 'bg-purple-100 text-purple-800',
  finance: 'bg-green-100 text-green-800',
  people: 'bg-blue-100 text-blue-800',
  system: 'bg-gray-100 text-gray-800',
};

const categoryIcons = {
  inventory: PackageIcon,
  finance: CurrencyDollarIcon,
  people: UsersIcon,
  system: GearIcon,
};

function NotificationsPage() {
  const [activeTab, setActiveTab] = useState('unread');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchNotifications = async (includeAcknowledged: boolean = false) => {
    setLoading(true);
    try {
      const response = await apiFetch(
        `/api/notifications/?include_acknowledged=${includeAcknowledged}&limit=100`
      );
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const acknowledgeNotification = async (notificationId: string) => {
    try {
      const response = await apiFetch(`/api/notifications/${notificationId}/acknowledge`, {
        method: 'POST',
      });

      if (response.ok) {
        toast.success('Notification acknowledged');
        // Refresh the list
        fetchNotifications(activeTab === 'all');
      }
    } catch (error) {
      console.error('Failed to acknowledge notification:', error);
      toast.error('Failed to acknowledge notification');
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (notification.action_url) {
      navigate({ to: notification.action_url });
    }
  };

  useEffect(() => {
    fetchNotifications(activeTab === 'all');
  }, [activeTab]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Notifications</h1>
            <p className="text-muted-foreground">
              Stay updated with important alerts and messages
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="unread">Unread</TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div>
                <div className="flex flex-col items-center justify-center py-12">
                  <BellIcon size={64} weight="duotone" className="text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No notifications</h3>
                  <p className="text-sm text-muted-foreground">
                    {activeTab === 'unread'
                      ? "You're all caught up!"
                      : 'No notifications to display'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`border-b-1 transition-all hover:shadow-md ${
                      notification.severity === 'critical' ? 'border-red-300' : ''
                    }`}
                  >
                    <div className="p-3">
                      <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className="flex-shrink-0 text-muted-foreground">
                          {(() => {
                            const IconComponent = categoryIcons[notification.category as keyof typeof categoryIcons];
                            return IconComponent ? <IconComponent size={40} weight="duotone" /> : <BellIcon size={40} weight="duotone" />;
                          })()}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          {/* Header */}
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg mb-1">
                                {notification.title}
                              </h3>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge
                                  variant="outline"
                                  className={categoryColors[notification.category as keyof typeof categoryColors]}
                                >
                                  {notification.category}
                                </Badge>
                                <Badge
                                  variant="outline"
                                  className={severityColors[notification.severity as keyof typeof severityColors]}
                                >
                                  {notification.severity}
                                </Badge>
                                <span className="text-sm text-muted-foreground">
                                  {formatDate(notification.created_at)}
                                </span>
                              </div>
                            </div>

                            {!notification.is_acknowledged && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => acknowledgeNotification(notification.id)}
                              >
                                <CheckIcon size={16} className="mr-1" />
                                Acknowledge
                              </Button>
                            )}
                            {/* Acknowledged Info */}
                            {notification.is_acknowledged && notification.acknowledged_at && (
                                <div className="mt-3 pt-3">
                                <p className="text-xs text-muted-foreground">
                                    Acknowledged
                                    {notification.acknowledged_by && ` by ${notification.acknowledged_by.full_name || notification.acknowledged_by.email}`}
                                    {` on`} {formatDate(notification.acknowledged_at)}
                                </p>
                                </div>
                            )}
                          </div>

                          {/* Message */}
                          <p className="text-muted-foreground mb-4">
                            {notification.message}
                          </p>

                          {/* Action Button */}
                          {notification.action_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleNotificationClick(notification)}
                            >
                              {notification.action_label || 'View Details'}
                            </Button>
                          )}


                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

export const Route = createFileRoute('/notifications')({
  component: NotificationsPage,
});
